// Who is signed in. Developers sign in with email and password, which gives a
// session token (a JWT); a pasted developer credential or the platform
// admin token also works. Tokens live in sessionStorage only (cleared when the
// tab closes) and the session ends after a period of inactivity.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiClient, ApiError, UNAUTHORIZED, adminApi, authApi, defaultBaseUrl, developerApi } from "./api";
import type { LoginChallenge, SignInResult } from "./types";

export type Role = "admin" | "developer";

export interface Session {
  role: Role;
  token: string;
  baseUrl: string;
  name: string;
  developerId?: string;
  /** "password" for an email sign-in session, "credential" for a pasted token. */
  via?: "password" | "credential";
  email?: string;
  /** The developer must verify their email before the console works. */
  verificationPending?: boolean;
  /** Sign-up only: false when the verification email couldn't be sent. */
  verificationEmailSent?: boolean;
}

const KEY = "gbe.session";
export const IDLE_LIMIT_MS = 1 * 60 * 60 * 1000;
/** Matches the engine's password policy. */
export const MIN_PASSWORD = 10;

const isJwt = (t: string) => t.startsWith("eyJ") && t.split(".").length === 3;

/** Identifies a pasted credential by its prefix. Game API keys are refused here. */
export function classifyCredential(token: string): Role | "game_key" | "unknown" {
  const t = token.trim();
  if (t.startsWith("gbe_dev_") || isJwt(t)) return "developer";
  if (t.startsWith("gbe_test_sk_") || t.startsWith("gbe_live_sk_")) return "game_key";
  if (t.length >= 8) return "admin";
  return "unknown";
}

function load(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function save(s: Session | null) {
  try {
    if (s) sessionStorage.setItem(KEY, JSON.stringify(s));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable: session lives in memory only */
  }
}

interface Ctx {
  session: Session | null;
  client: ApiClient | null;
  signIn: (token: string, baseUrl?: string) => Promise<Session>;
  /** Signs in, or returns the challenge when an emailed code is needed first. */
  signInWithPassword: (email: string, password: string) => Promise<{ session: Session } | { challenge: LoginChallenge }>;
  /** Finishes a sign-in with the emailed code. */
  verifyLoginCode: (challengeId: string, code: string) => Promise<Session>;
  register: (name: string, email: string, password: string) => Promise<Session>;
  signOut: (reason?: string) => void;
  signedOutReason: string | null;
  /** Re-reads the developer and clears the pending-verification state when verified. */
  refreshVerification: () => Promise<boolean>;
  /** Updates the name shown for the signed-in developer. */
  rename: (name: string) => void;
}

const SessionCtx = createContext<Ctx | null>(null);

function fromResult(r: SignInResult, baseUrl: string): Session {
  return {
    role: "developer", token: r.session.token, baseUrl, name: r.developer.name, developerId: r.developer.id,
    via: "password", email: r.developer.contact_email,
    verificationPending: !!r.developer.email_verification_pending, verificationEmailSent: r.verification_email_sent,
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(load);
  const [reason, setReason] = useState<string | null>(null);
  const lastActive = useRef(Date.now());

  const signOut = useCallback((why?: string) => {
    const current = load();
    // End the server-side session too; a credential is left for the developer to revoke.
    if (current?.via === "password") authApi(new ApiClient(current.baseUrl, current.token)).logout().catch(() => {});
    save(null);
    setSession(null);
    setReason(why ?? null);
  }, []);

  const start = useCallback((s: Session) => {
    save(s);
    lastActive.current = Date.now();
    setReason(null);
    setSession(s);
    return s;
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const baseUrl = defaultBaseUrl();
    const r = await authApi(new ApiClient(baseUrl, "")).login({ email: email.trim(), password });
    if (r.login_code_required) return { challenge: r.challenge };
    return { session: start(fromResult(r, baseUrl)) };
  }, [start]);

  const verifyLoginCode = useCallback(async (challengeId: string, code: string) => {
    const baseUrl = defaultBaseUrl();
    const r = await authApi(new ApiClient(baseUrl, "")).verifyLoginCode({ challenge_id: challengeId, code });
    return start(fromResult(r, baseUrl));
  }, [start]);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const baseUrl = defaultBaseUrl();
    const r = await authApi(new ApiClient(baseUrl, "")).register({ name: name.trim(), email: email.trim(), password });
    return start(fromResult(r, baseUrl));
  }, [start]);

  const signIn = useCallback(async (token: string, baseUrl = defaultBaseUrl()) => {
    token = token.trim();
    const kind = classifyCredential(token);
    if (kind === "game_key") {
      throw new ApiError(400, "game_key_not_allowed",
        "That is a game API key. Keys belong on your game servers; sign in with your email and password instead.");
    }
    if (kind === "unknown") throw new ApiError(400, "invalid_credential", "Paste a developer credential or the platform admin token.");
    const client = new ApiClient(baseUrl, token);
    let s: Session;
    if (kind === "developer") {
      const me = await developerApi(client).me();
      s = {
        role: "developer", token, baseUrl, name: me.name, developerId: me.id, via: "credential", email: me.contact_email,
        verificationPending: !!me.email_verification_pending,
      };
    } else {
      await adminApi(client).whoami();
      s = { role: "admin", token, baseUrl, name: "Platform admin" };
    }
    return start(s);
  }, [start]);

  const refreshVerification = useCallback(async () => {
    const current = load();
    if (!current || current.role !== "developer") return false;
    const me = await developerApi(new ApiClient(current.baseUrl, current.token)).me();
    if (me.email_verification_pending) return false;
    start({ ...current, verificationPending: false, verificationEmailSent: undefined });
    return true;
  }, [start]);

  const rename = useCallback((name: string) => {
    const current = load();
    if (current) start({ ...current, name });
  }, [start]);

  // A revoked or expired credential ends the session.
  useEffect(() => {
    const on = () => signOut("Your sign-in has expired or was ended elsewhere. Sign in again.");
    window.addEventListener(UNAUTHORIZED, on);
    return () => window.removeEventListener(UNAUTHORIZED, on);
  }, [signOut]);

  // Idle sign-out.
  useEffect(() => {
    if (!session) return;
    const bump = () => (lastActive.current = Date.now());
    const events = ["pointerdown", "keydown", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const timer = window.setInterval(() => {
      if (Date.now() - lastActive.current > IDLE_LIMIT_MS) signOut("You were signed out after 3 hours of inactivity.");
    }, 30_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(timer);
    };
  }, [session, signOut]);

  const client = useMemo(() => (session ? new ApiClient(session.baseUrl, session.token, { reportAuthFailures: true }) : null), [session]);
  const value = useMemo(() => ({ session, client, signIn, signInWithPassword, verifyLoginCode, register, signOut, signedOutReason: reason, refreshVerification, rename }),
    [session, client, signIn, signInWithPassword, verifyLoginCode, register, signOut, reason, refreshVerification, rename]);
  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): Ctx {
  const c = useContext(SessionCtx);
  if (!c) throw new Error("useSession outside SessionProvider");
  return c;
}

/** The signed-in API client; components using it render only when signed in. */
export function useClient(): ApiClient {
  const { client } = useSession();
  if (!client) throw new Error("not signed in");
  return client;
}
